import { useCallback } from "react";
import { useApi, useLiveData } from "../api/api";
import { GitHubAppStatus } from "../api/types";
import {
  AlertBanner,
  DescribeRow,
  PageTitle,
  Panel,
  btnSecondary,
} from "../api/controlPlaneUi";

const Pill: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
      ok ? "bg-emerald-950/40 text-emerald-400" : "bg-amber-950/40 text-amber-400"
    }`}
  >
    {children}
  </span>
);

const AdminPage: React.FC = () => {
  const api = useApi();
  const fetchStatus = useCallback(() => api.getGitHubStatus(), [api]);
  // Each check hits GitHub's GET /app, so poll rarely — the Recheck button covers
  // on-demand refresh (e.g. right after changing the App config).
  const { data, error, loading, refresh } = useLiveData<GitHubAppStatus>(
    fetchStatus,
    [fetchStatus],
    300000
  );

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Cluster" name="admin" />
      <p className="text-sm text-slate-500">
        Integration status and operational settings.
      </p>

      {error && (
        <AlertBanner variant="error">
          {error} — is the control plane running?
        </AlertBanner>
      )}

      <Panel
        title="GitHub App — pull requests"
        action={
          <button
            type="button"
            className={btnSecondary}
            onClick={refresh}
            disabled={loading}
          >
            Recheck
          </button>
        }
      >
        {loading && !data ? (
          <p className="text-sm text-slate-500 font-mono">Checking…</p>
        ) : data ? (
          <>
            <DescribeRow
              label="Configured"
              value={
                <Pill ok={data.configured}>
                  {data.configured ? "yes" : "no — set GH_APP_ID / GH_APP_RW_KEY"}
                </Pill>
              }
            />
            <DescribeRow
              label="Verified"
              value={
                <Pill ok={data.verified}>
                  {data.verified ? "credentials valid" : "not verified"}
                </Pill>
              }
            />
            <DescribeRow label="App ID" value={data.appId || "—"} />
            <DescribeRow
              label="App"
              value={data.appSlug ? <span className="font-mono">{data.appSlug}</span> : "—"}
            />
            {data.error && (
              <DescribeRow
                label="Error"
                value={<span className="text-red-600 font-mono text-xs">{data.error}</span>}
              />
            )}
            <p className="mt-2 text-xs text-slate-500">
              Opens real pull requests in code from a completed task's change-set
              (scoped App: Contents + Pull requests, on granted repos). The token
              never reaches the loop or an agent.
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-400">No status.</p>
        )}
      </Panel>
    </div>
  );
};

export default AdminPage;
